import { useState } from 'react';
import { Head } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { Button } from '../../components/Button';
import { getCSRFToken } from '../../utils/csrf';

type Status = 'pending' | 'not_found' | 'expired' | 'already_accepted' | 'already_member' | 'wrong_email';

interface InvitationInfo {
    token: string;
    organizationName: string;
    role: string;
    roleName: string;
    inviterName: string;
    expiresAt: string;
}

interface Props {
    status: Status;
    invitation?: InvitationInfo;
    acceptUrl?: string;
    organizationName?: string;
    expectedEmail?: string;
}

export default function InvitationShow({ status, invitation, acceptUrl, organizationName, expectedEmail }: Props) {
    const [accepting, setAccepting] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [acceptedOrgName, setAcceptedOrgName] = useState<string | null>(null);

    const handleAccept = async () => {
        if (!acceptUrl) return;
        setAccepting(true);
        setError(null);
        try {
            const res = await fetch(acceptUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-XSRF-TOKEN': getCSRFToken(),
                },
            });
            const data = await res.json();
            if (data.success) {
                setAccepted(true);
                setAcceptedOrgName(data.organizationName);
            } else {
                setError(data.message || 'Error inesperado.');
            }
        } catch {
            setError('Error de conexion. Intentá de nuevo.');
        } finally {
            setAccepting(false);
        }
    };

    return (
        <MainLayout>
            <Head>
                <title>Invitacion</title>
                <meta name="robots" content="noindex" />
            </Head>

            <div className="mx-auto max-w-lg px-4 py-16">
                <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm text-center">
                    {accepted ? (
                        <AcceptedState orgName={acceptedOrgName!} />
                    ) : status === 'pending' && invitation ? (
                        <PendingState
                            invitation={invitation}
                            accepting={accepting}
                            error={error}
                            onAccept={handleAccept}
                        />
                    ) : (
                        <ErrorState status={status} organizationName={organizationName} expectedEmail={expectedEmail} />
                    )}
                </div>
            </div>
        </MainLayout>
    );
}

function AcceptedState({ orgName }: { orgName: string }) {
    return (
        <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <i className="fa fa-check text-2xl text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Te uniste a {orgName}</h1>
            <p className="text-sm text-gray-600 mb-6">
                Ya podés acceder a la organizacion desde tu cuenta.
            </p>
            <a
                href="/"
                className="inline-block rounded-lg bg-green-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-green-700"
            >
                Ir al inicio
            </a>
        </>
    );
}

function PendingState({ invitation, accepting, error, onAccept }: {
    invitation: InvitationInfo;
    accepting: boolean;
    error: string | null;
    onAccept: () => void;
}) {
    return (
        <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <i className="fa fa-building text-2xl text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
                Te invitaron a {invitation.organizationName}
            </h1>
            <p className="text-sm text-gray-600 mb-1">
                {invitation.inviterName
                    ? <><strong>{invitation.inviterName}</strong> te invitó como <strong>{invitation.roleName}</strong>.</>
                    : <>Fuiste invitado como <strong>{invitation.roleName}</strong>.</>
                }
            </p>
            <p className="text-xs text-gray-400 mb-6">
                La invitacion vence el {invitation.expiresAt}.
            </p>

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            <Button onClick={onAccept} disabled={accepting}>
                {accepting ? 'Aceptando...' : 'Aceptar invitacion'}
            </Button>
        </>
    );
}

function ErrorState({ status, organizationName, expectedEmail }: { status: Status; organizationName?: string; expectedEmail?: string }) {
    const config: Record<string, { icon: string; iconBg: string; iconColor: string; title: string; message: string }> = {
        not_found: {
            icon: 'fa-question-circle',
            iconBg: 'bg-gray-100',
            iconColor: 'text-gray-400',
            title: 'Invitacion no encontrada',
            message: 'Este enlace de invitacion no es valido o fue eliminado.',
        },
        expired: {
            icon: 'fa-clock',
            iconBg: 'bg-yellow-100',
            iconColor: 'text-yellow-600',
            title: 'Invitacion vencida',
            message: `La invitacion a ${organizationName || 'la organizacion'} expiró. Pedí al administrador que te envíe una nueva.`,
        },
        already_accepted: {
            icon: 'fa-check-circle',
            iconBg: 'bg-blue-100',
            iconColor: 'text-blue-600',
            title: 'Invitacion ya aceptada',
            message: `Esta invitacion a ${organizationName || 'la organizacion'} ya fue aceptada.`,
        },
        already_member: {
            icon: 'fa-users',
            iconBg: 'bg-blue-100',
            iconColor: 'text-blue-600',
            title: 'Ya sos miembro',
            message: `Ya sos miembro de ${organizationName || 'esta organizacion'}.`,
        },
        wrong_email: {
            icon: 'fa-envelope',
            iconBg: 'bg-red-100',
            iconColor: 'text-red-600',
            title: 'Email incorrecto',
            message: `Esta invitación fue enviada a ${expectedEmail || 'otro email'}. Iniciá sesión con esa cuenta para aceptarla.`,
        },
    };

    const c = config[status] || config.not_found;

    return (
        <>
            <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${c.iconBg}`}>
                <i className={`fa ${c.icon} text-2xl ${c.iconColor}`} />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">{c.title}</h1>
            <p className="text-sm text-gray-600 mb-6">{c.message}</p>
            <a
                href="/"
                className="inline-block rounded-lg bg-green-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-green-700"
            >
                Ir al inicio
            </a>
        </>
    );
}
